import React, { useEffect, useState } from 'react';
import { CardContent } from "@/components/ui/card";
import { useTenancy } from "@/contexts/TenancyContext";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, AlertCircle } from "lucide-react";
import { collection, getDocs, getFirestore, Timestamp } from "firebase/firestore";
import { cleanDateAndTimeObject } from '@shared/renderDate';
import { useWidgetStatusReporter } from "@/contexts/DashboardWidgetContext";
import "./widgets.css";

interface CorrectiveActionItem {
  id: string;
  summary: string;
  status: "Open" | "Closed";
  owner: string;
  ownerName: string;
  dueDate: Date | null;
}

const CorrectiveActionsListWidget: React.FC = () => {
  const { activeTenancy, userIdToName } = useTenancy();
  const [actions, setActions] = useState<CorrectiveActionItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Report widget status to dashboard context
  useWidgetStatusReporter('corrective-actions-list', isLoading, error, actions.length > 0);

  const fetchCorrectiveActions = async () => {
    if (!activeTenancy?.id || activeTenancy.id === "Loading...") {
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      const db = getFirestore();
      const correctiveActionsRef = collection(db, `customers/${activeTenancy.id}/correctiveActions`);
      const snapshot = await getDocs(correctiveActionsRef);
      
      const actionsList: CorrectiveActionItem[] = snapshot.docs.map(doc => {
        const data = doc.data();
        
        // Parse due date
        let dueDate: Date | null = null;
        if (data.dueDate) {
          if (data.dueDate instanceof Timestamp) {
            dueDate = data.dueDate.toDate();
          } else if (data.dueDate instanceof Date) {
            dueDate = data.dueDate;
          } else if (typeof data.dueDate === 'string') {
            const parsed = new Date(data.dueDate);
            if (!isNaN(parsed.getTime())) {
              dueDate = parsed;
            }
          } else if (data.dueDate?.seconds) {
            dueDate = new Date(data.dueDate.seconds * 1000);
          }
        }
        
        // Get owner name
        const ownerId = data.owner || '';
        const ownerName = userIdToName?.[ownerId] || 'Unassigned';
        
        return {
          id: doc.id,
          summary: data.summary || 'Untitled',
          status: data.status || 'Open',
          owner: ownerId,
          ownerName: ownerName === '' || ownerName === 'Deleted User' ? 'Unassigned' : ownerName,
          dueDate
        };
      });
      
      // Sort by due date (earliest first), then by status (Open first)
      actionsList.sort((a, b) => {
        // Open items first
        if (a.status !== b.status) {
          return a.status === 'Open' ? -1 : 1;
        }
        // Then by due date
        if (a.dueDate && b.dueDate) {
          return a.dueDate.getTime() - b.dueDate.getTime();
        }
        if (a.dueDate) return -1;
        if (b.dueDate) return 1;
        return 0;
      });
      
      setActions(actionsList);
    } catch (err) {
      console.error('[CorrectiveActionsListWidget] Error fetching corrective actions:', err);
      setError('Failed to load corrective actions');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchCorrectiveActions();
  }, [activeTenancy?.id, userIdToName]);

  const handleRefresh = () => {
    fetchCorrectiveActions();
  };

  const formatDueDate = (date: Date | null): string => {
    if (!date) return 'No due date';
    return date.toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const isOverdue = (date: Date | null, status: string): boolean => {
    if (!date || status === 'Closed') return false;
    return date.getTime() < Date.now();
  };

  const getDaysOverdue = (date: Date | null): number => {
    if (!date) return 0;
    return Math.ceil((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
  };

  // Loading state
  if (isLoading) {
    return (
      <CardContent className="h-full w-full flex flex-col" style={{ containerType: 'inline-size', padding: 'min(2cqw, 1rem)' }}>
        <div className="w-full flex justify-between items-center mb-2">
          <h3 className="text-lg font-semibold">Corrective Actions</h3>
        </div>
        <div className="flex flex-col gap-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="p-2 rounded-md border border-border">
              <Skeleton className="h-4 w-3/4 mb-2" />
              <div className="flex justify-between">
                <Skeleton className="h-3 w-20" />
                <Skeleton className="h-3 w-24" />
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    );
  }

  // Error state
  if (error) {
    return (
      <CardContent className="h-full w-full flex flex-col items-center justify-center" style={{ containerType: 'inline-size' }}>
        <h3 className="text-lg font-semibold mb-2">Corrective Actions</h3>
        <div className="flex flex-col items-center justify-center py-4">
          <AlertCircle className="h-8 w-8 text-red-500 mb-2" />
          <p className="text-red-500 text-sm text-center">{error}</p>
          <button 
            onClick={handleRefresh}
            className="mt-2 text-sm text-blue-500 underline"
          >
            Try again
          </button>
        </div>
      </CardContent>
    );
  }

  return (
    <CardContent className="h-full w-full flex flex-col" style={{ containerType: 'inline-size', padding: 'min(2cqw, 1rem)' }}>
      <div className="w-full flex justify-between items-center mb-2 flex-shrink-0">
        <h3 className="text-lg font-semibold truncate pr-2">Corrective Actions</h3>
        {/* <button 
          onClick={handleRefresh}
          disabled={isLoading}
          className="p-1 rounded-full hover:bg-muted transition-colors"
          aria-label="Refresh corrective actions"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
        </button> */}
      </div>
      
      <div className="flex-1 overflow-hidden flex flex-col">
        {actions.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center py-4">
            <h1 style={{ fontSize: "2rem" }}>✅</h1>
            <p className="text-sm text-muted-foreground text-center px-2">
              No corrective actions found
            </p>
          </div>
        ) : (
          <div className="overflow-y-auto pr-1 -mr-1">
            <ul className="w-full flex flex-col gap-2">
              {actions.slice(0, 10).map((action) => (
                <li
                  key={action.id}
                  className="rounded-md border border-border bg-background p-2 hover:bg-accent/50 transition"
                >
                  <div className="font-medium text-xs flex flex-col gap-1">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold truncate flex-1">
                        {action.summary}
                      </span>
                      <Badge 
                        variant={action.status === "Open" ? "destructiveSubtle" : "successSubtle"}
                        className="text-xs shrink-0"
                      >
                        {action.status}
                      </Badge>
                    </div>
                    <div className="flex items-center justify-between text-muted-foreground text-xs">
                      <span className="truncate">
                        {action.ownerName}
                      </span>
                      <span className={`whitespace-nowrap ${isOverdue(action.dueDate, action.status) ? 'text-red-600 font-medium' : ''}`}>
                        {formatDueDate(action.dueDate)}
                        {isOverdue(action.dueDate, action.status) && (
                          <span className="ml-1">
                            ({getDaysOverdue(action.dueDate)}d overdue)
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
            {actions.length > 10 && (
              <p className="text-xs text-muted-foreground text-center mt-2">
                +{actions.length - 10} more
              </p>
            )}
          </div>
        )}
      </div>
    </CardContent>
  );
};

export default CorrectiveActionsListWidget;
